import { z } from 'zod';
import { TxType } from './tx-type';

/**
 * 진행 중인 트랜잭션의 현재 단계.
 *
 * `waiting`은 계약에만 있고 **아직 아무도 만들지 않는다**. 에이전트 레지스트리의 `stateOf`는
 * 열린 자식이 SQL이면 `sql`, 외부 호출이면 `ext`, 없으면 `running`만 낸다. 풀 대기·잠금
 * 대기를 자식 span 없이 알아낼 방법이 생기면 그때 채운다. 화면은 모르는 값을 받아도
 * 깨지지 않아야 하므로 열거는 그대로 둔다.
 */
export const ActiveTxState = z.enum(['running', 'sql', 'ext', 'waiting']);
export type ActiveTxState = z.infer<typeof ActiveTxState>;

/**
 * 진입점·현재 span 이름의 상한(글자 수, 57회차). **넘으면 거절하지 않고 잘라 받는다.**
 *
 * 에이전트는 `http.route`가 아직 없으면 `url.path`·`http.target`(쿼리 포함, 자르지 않음)을 진입점으로
 * 쓴다. Node 헤더 상한이 16KB라 긴 URL 요청 하나가 진행 중이기만 해도 그 1초 스냅샷 전체가 거절되면,
 * 대상 앱에 긴 URL을 계속 보내 실시간을 끌 수 있다. 그래서 여기만 자르기다
 */
export const ACTIVE_TX_TEXT_MAX = 4_096;

/** 테넌트 id 상한(글자 수, 57회차). 넘으면 거절한다. 실제 값은 정수 id다 */
export const ACTIVE_TX_TENANT_MAX = 256;

const clipped = z.string().transform((value) => value.slice(0, ACTIVE_TX_TEXT_MAX));

/**
 * 진행 중인 요청 한 건. 에이전트 레지스트리의 항목이며 1초 스냅샷에 실린다.
 */
export const ActiveTx = z.object({
  txId: z.string().regex(/^[0-9a-f]{16}$/),
  traceId: z.string().regex(/^[0-9a-f]{32}$/),
  txType: TxType,
  entry: z.string().min(1).pipe(clipped),
  /** 스냅샷 시점 기준 경과 시간 */
  elapsedMs: z.number().nonnegative(),
  state: ActiveTxState,
  /** 현재 열려 있는 가장 안쪽 span 이름. 없으면 생략 */
  currentSpan: clipped.optional(),
  tenantId: z.string().max(ACTIVE_TX_TENANT_MAX).optional(),
});
export type ActiveTx = z.infer<typeof ActiveTx>;
